import {
  createSlice,
  createSelector,
  createAsyncThunk
} from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { set } from 'lodash';

import FungibleTokens, {
  fungibleTokensService
} from 'services/fungible-tokens';

import { wallet } from 'utils/wallet';

import config from 'config';
const {
  OPN_CONTRACT,
  OCC_CONTRACT,
  USDC_CONTRACT,
  FT_MINIMUM_STORAGE_BALANCE_LARGE,
  METHODS
} = config;

type AccountTypes = {
  accountId: string;
  contractId: string;
  accountExists?: boolean;
  authReady: boolean;
  loadingContracts: boolean;
  registerInContracts?: {
    [key: string]: {
      value: boolean;
      min: string;
    };
  };
  balance?: {
    [key: string]: string;
  };
  storageBalance?: {
    [key: string]: {
      total: string,
      available: string,
    }
  }
};

const SLICE_NAME = 'account';

const initialRegisterInContracts = {
  [OPN_CONTRACT]: {
    value: false,
    min: FT_MINIMUM_STORAGE_BALANCE_LARGE
  },
  [OCC_CONTRACT]: {
    value: false,
    min: FT_MINIMUM_STORAGE_BALANCE_LARGE
  },
  ...(USDC_CONTRACT
    ? {
        [USDC_CONTRACT]: {
          value: false,
          min: FT_MINIMUM_STORAGE_BALANCE_LARGE
        }
      }
    : {})
};

const initialStorageBalance = {
  [OPN_CONTRACT]: { total: '0', available: '0' },
  [OCC_CONTRACT]: { total: '0', available: '0' },
  ...(USDC_CONTRACT
    ? {
        [USDC_CONTRACT]: { total: '0', available: '0' }
      }
    : {})
};

const initialBalance = {
  available: '0',
  total: '0',
  stateStaked: '0',
  staked: '0',
  balanceAvailable: '0'
};

const initialState: AccountTypes = {
  accountId: '',
  contractId: '',
  accountExists: false,
  authReady: false,
  loadingContracts: true,
  registerInContracts: initialRegisterInContracts,
  balance: initialBalance,
  storageBalance: initialStorageBalance,
};

export const actionAccountBalance = createAsyncThunk(
  'getBalance',
  async () => {
    try {
      return await wallet.getBalance();
    } catch (error) {
      console.warn('Error loading balance:', error.message);
      throw error;
    }
  }
);

export const actionAccountOwner = createAsyncThunk(
  'refreshAccountOwner',
  async () => {
    try {
      return await wallet.loadAccount();
    } catch (error) {
      console.warn('Error loading account:', error.message);
      throw error;
    }
  }
);

export const actionAccountCheckBalance = createAsyncThunk(
  'checkAccountLogin',
  async (
    {
      contractName,
      accountId
    }: {
      contractName: string;
      accountId: string;
    },
    thunkAPI
  ) => {
    const {
      actions: { isContractsBalance }
    } = accountSlice;
    const { dispatch } = thunkAPI;
    try {
      const { min } = await FungibleTokens.getViewMethod({
        contractName,
        accountId,
        methodName: METHODS.storage_balance_bounds
      });

      const isStorageBalance =
        await fungibleTokensService.isStorageBalanceAvailable({
          contractName,
          accountId
        });
      dispatch(
        isContractsBalance({
          contractName,
          value: isStorageBalance,
          min
        })
      );
    } catch (e) {
      console.warn(`Failed to load FT for ${contractName}`, e);
    }
  }
);

export const actionAccountSetStorageBalance = createAsyncThunk(
  'setStorageBalance',
  async ({ contractName, accountId } : { contractName: string, accountId: string }) => {
    const storageBalance = await fungibleTokensService.getStorageBalance({
      contractName,
      accountId
    });
    return {
      [contractName]: storageBalance || initialStorageBalance[contractName]
    };
  }
)

const accountSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setAccountId(state, { payload }: PayloadAction<{ accountId: string }>) {
      const { accountId } = payload;
      set(state, 'accountId', accountId);
      // Wait for storage registration checks before prompting Register modal.
      set(state, 'loadingContracts', true);
      set(state, 'registerInContracts', initialRegisterInContracts);
    },
    setAuthReady(state, { payload }: PayloadAction<boolean>) {
      set(state, 'authReady', payload);
    },
    isContractsBalance(state, { payload }) {
      const { contractName, value, min } = payload;
      set(state, ['registerInContracts', contractName], {
        value,
        min
      });
    },
    actionAccountLoading(state) {
      set(state, 'loadingContracts', false);
    },
    clearAccount(state) {
      set(state, 'accountId', '');
      set(state, 'accountExists', false);
      set(state, 'loadingContracts', true);
      set(state, 'registerInContracts', initialRegisterInContracts);
      set(state, 'balance', initialBalance);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(actionAccountBalance.fulfilled, (state, { payload }) => {
      return {
        ...state,
        balance: {
          ...state.balance,
          ...payload
        }
      };
    });
    builder.addCase(actionAccountOwner.fulfilled, (state, { payload }) => {
      return {
        ...state,
        ...payload,
        accountExists: true,
        balance: {
          ...payload.balance,
          ...state.balance
        }
      };
    });
    builder.addCase(actionAccountSetStorageBalance.fulfilled, (state, { payload }) => {
      state.storageBalance = {
        ...state.storageBalance,
        ...payload,
      };
    })
  }
});

export default accountSlice;

export const actions = {
  ...accountSlice.actions,
  actionAccountCheckBalance,
  actionAccountSetStorageBalance,
  actionAccountOwner,
  actionAccountBalance
};

export const selectAccountSlice = (state: any) => state[SLICE_NAME];

export const selectAccountId = createSelector(
  selectAccountSlice,
  (account) => account.accountId
);

export const selectStorageBalance = createSelector(
  selectAccountSlice,
  (account) => account.storageBalance
);

export const selectBalance = createSelector(
  selectAccountSlice,
  (account) => {
    return account.balance || {}
  }
);

export const selectAvailableBalance = createSelector(
  selectBalance,
  (balance) => balance.balanceAvailable
);

export const selectRegisterContracts = createSelector(
  selectAccountSlice,
  (account) => account.registerInContracts || initialRegisterInContracts
);

export const selectAccountLoadingContracts = createSelector(
  [selectAccountSlice],
  (account) => account.loadingContracts
);

export const selectAuthReady = createSelector(
  [selectAccountSlice],
  (account) => account.authReady
);
